import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTest, useUpdateTest } from '../hooks/useTests';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Card from '../components/Card';
import Skeleton from '../components/Skeleton';
import { ChevronLeft } from 'lucide-react';

export default function EditTest() {
  const navigate = useNavigate();
  const { packId, testId } = useParams<{ packId: string; testId: string }>();
  const { data: test, isLoading } = useTest(packId || '', testId || '');
  const updateTest = useUpdateTest();

  const [formData, setFormData] = useState({
    test_date: '',
    test_operator: '',
    c_capacity: '',
    d_capacity: '',
    charge_cutoff_voltage: '',
    discharge_cutoff_voltage: '',
    soc: '',
    cycle: '',
    pack_type: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-fill form when test data loads
  useEffect(() => {
    if (test) {
      setFormData({
        test_date: test.test_date ? test.test_date.split('T')[0] : '',
        test_operator: test.test_operator || '',
        c_capacity: test.c_capacity || '',
        d_capacity: test.d_capacity || '',
        charge_cutoff_voltage: test.charge_cutoff_voltage || '',
        discharge_cutoff_voltage: test.discharge_cutoff_voltage || '',
        soc: test.soc || '',
        cycle: test.cycle || '',
        pack_type: test.pack_type || '',
      });
    }
  }, [test]);

  const cycleOptions = [
    { value: '', label: 'Select cycle...' },
    { value: '1st Run', label: '1st Run' },
    { value: '2nd Run', label: '2nd Run' },
    { value: '3rd Run', label: '3rd Run' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!packId || !testId) return;

    try {
      await updateTest.mutateAsync({
        packId,
        testId,
        data: {
          test_date: formData.test_date ? `${formData.test_date}T00:00:00.000Z` : undefined,
          test_operator: formData.test_operator || undefined,
          c_capacity: formData.c_capacity || undefined,
          d_capacity: formData.d_capacity || undefined,
          charge_cutoff_voltage: formData.charge_cutoff_voltage || undefined,
          discharge_cutoff_voltage: formData.discharge_cutoff_voltage || undefined,
          soc: formData.soc || undefined,
          cycle: formData.cycle || undefined,
          pack_type: formData.pack_type || undefined,
        },
      });
      navigate(`/packs/${packId}`);
    } catch (error) {
      setErrors({ submit: 'Failed to update test. Please try again.' });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton variant="circle" className="w-8 h-8" />
          <Skeleton className="h-6 w-32" />
        </div>
        <Card className="!p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </Card>
      </div>
    );
  }

  if (!test) {
    return (
      <Card>
        <div className="text-center py-12">
          <p className="text-lg font-semibold text-gray-700 mb-2">Test not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/packs/${packId}`)}
          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">Edit Test</h1>
          <p className="text-xs text-gray-500">Pack: {packId}</p>
        </div>
      </div>

      <Card className="!p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Test Date"
              type="date"
              value={formData.test_date}
              onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
            />
            <Input
              label="Test Operator"
              value={formData.test_operator}
              onChange={(e) => setFormData({ ...formData, test_operator: e.target.value })}
              placeholder="Operator name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Cycle"
              options={cycleOptions}
              value={formData.cycle}
              onChange={(e) => setFormData({ ...formData, cycle: e.target.value })}
            />
            <Input
              label="Pack Type"
              value={formData.pack_type}
              onChange={(e) => setFormData({ ...formData, pack_type: e.target.value })}
              placeholder="e.g., Fresh Pack"
            />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Measurements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Charge Capacity"
                value={formData.c_capacity}
                onChange={(e) => setFormData({ ...formData, c_capacity: e.target.value })}
                placeholder="Charge capacity"
              />
              <Input
                label="Discharge Capacity"
                value={formData.d_capacity}
                onChange={(e) => setFormData({ ...formData, d_capacity: e.target.value })}
                placeholder="Discharge capacity"
              />
              <Input
                label="Charge Cutoff Voltage"
                value={formData.charge_cutoff_voltage}
                onChange={(e) => setFormData({ ...formData, charge_cutoff_voltage: e.target.value })}
                placeholder="Voltage"
              />
              <Input
                label="Discharge Cutoff Voltage"
                value={formData.discharge_cutoff_voltage}
                onChange={(e) => setFormData({ ...formData, discharge_cutoff_voltage: e.target.value })}
                placeholder="Voltage"
              />
              <Input
                label="State of Charge (SOC)"
                value={formData.soc}
                onChange={(e) => setFormData({ ...formData, soc: e.target.value })}
                placeholder="Percentage"
              />
            </div>
          </div>

          {errors.submit && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded p-3">
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              disabled={updateTest.isPending}
              className="flex-1"
            >
              {updateTest.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/packs/${packId}`)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
